import { ref, computed, watch, nextTick } from 'vue';
import { storeToRefs } from 'pinia';
import { useWalletStore } from '@/store/wallet';
import { notify } from 'notiwind';
import {
    COPYABLE_DETAILS,
    WELCOME_BONUS_DETAILS,
    PRODUCT_CHANGED_DETAILS,
} from '@/components/wallet/constants/card-details';

export function useCopyCardWizard(emit) {
    const walletStore = useWalletStore();
    const { copyCardDetails, setSelectedCard, getWalletByEntities } = walletStore;
    const { selectedCard } = storeToRefs(walletStore);

    const currentStep = ref(1);
    const selectedReceivingCard = ref(null);
    const selectAll = ref(false);
    const confirmChanges = ref(false);

    const errors = ref({
        receivingCard: [],
        selectedCard: [],
        madeSelections: [],
        confirmChanges: [],
    });

    const selectedDetails = ref({
        card_status: false,
        date_opened: false,
        account_type: false,
        credit_limit: false,
        annual_fee: false,
        ignore: false,
        product_changed: false,
        welcome_bonus: false,
        bonus_offers: false,
    });

    const selectedSubDetails = ref({
        received: false,
        date_received: false,
        bonus_amount: false,
        minimum_spend_amount: false,
        minimum_spend_deadline: false,
        type: false,
        original_card: false,
        product_changed_date_opened: false,
        product_changed_date: false,
        product_changed_bonus_amount: false,
        product_changed_min_spend: false,
    });

    const subheaderText = computed(() => {
        if (currentStep.value === 1) {
            return 'Select the source and destination cards.';
        }
        if (currentStep.value === 3) {
            return 'Review and confirm';
        }
        return 'Select details to copy. New properties will be copied, existing ones will be overridden.';
    });

    const copyableDetails = computed(() => {
        if (!selectedCard.value?.user_managed_annual_fee) {
            return COPYABLE_DETAILS.filter((d) => d.key !== 'annual_fee');
        }
        return COPYABLE_DETAILS;
    });

    const welcomeBonusDetails = computed(() => WELCOME_BONUS_DETAILS);
    const productChangedDetails = computed(() => PRODUCT_CHANGED_DETAILS);

    const detailsToCopy = computed(() => [
        ...copyableDetails.value.filter((detail) => selectedDetails.value[detail.key]),
        ...welcomeBonusDetails.value.filter((detail) => selectedSubDetails.value[detail.key]),
        ...productChangedDetails.value.filter((detail) => selectedSubDetails.value[detail.key]),
    ]);

    const validateAndCollectErrors = () => {
        errors.value = {
            receivingCard: [],
            selectedCard: [],
            confirmChanges: [],
            madeSelections: [],
        };

        if (currentStep.value === 1 || currentStep.value === 2) {
            if (!selectedCard.value) {
                errors.value.selectedCard = [{ message: 'Select a card to continue' }];
                return false;
            }
            if (!selectedReceivingCard.value) {
                errors.value.receivingCard = [{ message: 'Select a card to continue' }];
                return false;
            }
        }

        if (currentStep.value === 2) {
            if (!Object.values(selectedDetails.value).some((val) => val === true)) {
                errors.value.madeSelections = [{ message: 'Select at least one detail to continue' }];
                return false;
            }
        }

        if (currentStep.value === 3 && !confirmChanges.value) {
            errors.value.confirmChanges = [{ message: 'Confirm to continue' }];
            return false;
        }

        return true;
    };

    const originalBonusOffers = computed(() => {
        if (selectedCard.value?.product_changed) {
            const productChangedBonusOffers = selectedCard.value.bonus_offers?.filter(
                (offer) => offer.product_changed
            );
            if (productChangedBonusOffers?.length > 0) return productChangedBonusOffers;
        }
        return [];
    });

    const nextStep = async () => {
        if (!validateAndCollectErrors()) return;
        if (currentStep.value === 3) {
            await doCopyDetails();
        } else {
            currentStep.value++;
        }
    };

    const setReceivingCard = ({ account }) => {
        selectedReceivingCard.value = account || null;
    };

    const setCardToCopy = (card) => {
        setSelectedCard(card ? card.account : null);
    };

    const toggleSelectAll = () => {
        errors.value.madeSelections = [];
        Object.keys(selectedDetails.value).forEach((key) => {
            selectedDetails.value[key] = selectAll.value;
        });
        if (selectAll.value && selectedDetails.value.welcome_bonus) {
            welcomeBonusDetails.value.forEach((detail) => {
                selectedSubDetails.value[detail.key] = true;
            });
        }
    };

    const onDetailToggled = () => {
        errors.value.madeSelections = [];
        const visibleKeys = copyableDetails.value.map((d) => d.key);
        selectAll.value = visibleKeys.every((key) => selectedDetails.value[key] === true);
    };

    const isSelected = (key) => detailsToCopy.value.some((d) => d.key === key);

    const prevStepOrCancel = () => {
        if (currentStep.value === 2) resetValues();
        if (currentStep.value > 1) {
            currentStep.value--;
        } else {
            closeModal();
        }
    };

    const doCopyDetails = async () => {
        try {
            const cardToUpdate = { ...selectedReceivingCard.value };
            let data = {};

            detailsToCopy.value.forEach((detail) => {
                cardToUpdate[detail.key] = selectedCard.value[detail.key];

                if (detail.key === 'product_changed' && selectedCard.value['product_changed'] === true) {
                    cardToUpdate.original_card_id = selectedCard.value.original_card_id;
                }
                if (detail.key === 'annual_fee') {
                    cardToUpdate.user_managed_annual_fee = true;
                }
                if (detail.key === 'bonus_offers') {
                    data['bonus_offers'] = selectedCard.value.bonus_offers;
                }
                if (detail.key === 'welcome_bonus') {
                    data['welcome_bonus'] = selectedCard.value.welcome_bonus;
                }
            });

            data = { card: cardToUpdate, ...data };
            await copyCardDetails(data, selectedReceivingCard.value.id);
            await getWalletByEntities(null, { background: true });
            notify({ group: 'success', title: 'Success', text: 'Card details copied successfully.' }, 4000);
            closeModal();
        } catch (error) {
            console.error('Error updating card details:', error);
            notify({
                group: 'error',
                title: 'Error',
                text: error?.response?.data?.message || 'Failed to copy card details.',
            }, 4000);
        }
    };

    const closeModal = () => {
        emit('close');
        currentStep.value = 1;
        setSelectedCard(null);
        resetValues();
    };

    const resetValues = () => {
        selectedReceivingCard.value = null;
        selectAll.value = false;
        confirmChanges.value = false;
        Object.keys(selectedDetails.value).forEach((key) => { selectedDetails.value[key] = false; });
        Object.keys(selectedSubDetails.value).forEach((key) => { selectedSubDetails.value[key] = false; });
    };

    // Watchers
    watch([() => selectedCard.value, () => selectedReceivingCard.value], () => {
        if (selectedCard.value) errors.value.selectedCard = [];
        if (selectedReceivingCard.value) errors.value.receivingCard = [];
        if (currentStep.value === 1 && selectedCard.value && selectedReceivingCard.value) {
            nextStep();
        }
    });

    watch(
        [() => selectedDetails.value.welcome_bonus, () => selectedDetails.value.product_changed],
        ([welcome_bonus, product_changed]) => {
            selectedSubDetails.value.received = welcome_bonus;
            selectedSubDetails.value.date_received = welcome_bonus;
            selectedSubDetails.value.bonus_amount = welcome_bonus;
            selectedSubDetails.value.minimum_spend_amount = welcome_bonus;
            selectedSubDetails.value.minimum_spend_deadline = welcome_bonus;
            selectedSubDetails.value.type = welcome_bonus;
            selectedSubDetails.value.original_card = product_changed;
            selectedSubDetails.value.product_changed_date_opened = product_changed;
            selectedSubDetails.value.product_changed_date = product_changed;
            selectedSubDetails.value.product_changed_bonus_amount = product_changed;
            selectedSubDetails.value.product_changed_min_spend = product_changed;
        },
        { immediate: true }
    );

    watch(errors, (newErrors) => {
        const hasErrors = Object.values(newErrors).some((errorArray) => errorArray.length > 0);
        if (hasErrors) {
            nextTick(() => {
                const firstError = document.querySelector('.error-message');
                if (firstError) {
                    firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            });
        }
    }, { deep: true });

    return {
        selectedCard,
        currentStep,
        selectedReceivingCard,
        selectAll,
        confirmChanges,
        errors,
        selectedDetails,
        selectedSubDetails,
        subheaderText,
        copyableDetails,
        welcomeBonusDetails,
        productChangedDetails,
        detailsToCopy,
        originalBonusOffers,
        nextStep,
        setReceivingCard,
        setCardToCopy,
        toggleSelectAll,
        onDetailToggled,
        isSelected,
        prevStepOrCancel,
        closeModal,
        resetValues,
    };
}
